// components/media/MediaManager.js
import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  message,
  Modal,
  Input,
  Tabs,
  Space
} from 'antd';
import { 
  PlusOutlined, 
  CloudUploadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUserMedia,
  getEntityMedia,
  updateMedia,
  getMediaStats,
  clearError,
  clearSuccess,
  setPagination
} from '../../features/media/mediaSlice';
import { getDesProjects } from '../../features/projects/desprojectSlice';
import { getDevProjects } from '../../features/projects/devprojectSlice';
import { getBlogs } from '../../features/blog/blogSlice';

// Import the new components
import Overview from './Overview';
import SelectedEntity from './SelectedEntity';
import Trash from './Trash';
import SelectProjOrBlog from './SelectProjOrBlog';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const MediaManager = () => {
  const dispatch = useDispatch();
  const { 
    pagination, 
    filters, 
    loading, 
    processing, 
    error, 
    success 
  } = useSelector((state) => state.media);

  const [editingMedia, setEditingMedia] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entitySelectorVisible, setEntitySelectorVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Load all entities when component mounts
    dispatch(getDesProjects());
    dispatch(getDevProjects());
    dispatch(getBlogs());
    dispatch(getMediaStats());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'overview') {
      dispatch(getUserMedia({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      }));
    } else if (activeTab === 'trash') {
      // Load deleted items when trash tab is active
      dispatch(getUserMedia({
        page: pagination.current,
        limit: pagination.pageSize,
        status: 'deleted',
        entityType: filters.entityType !== 'all' ? filters.entityType : undefined,
        mediaType: filters.mediaType !== 'all' ? filters.mediaType : undefined,
        search: filters.search
      }));
    }
  }, [dispatch, pagination.current, pagination.pageSize, filters, activeTab]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Refresh data
  const handleRefresh = () => {
    if (activeTab === 'overview') {
      dispatch(getUserMedia({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      }));
    } else if (activeTab === 'trash') {
      dispatch(getUserMedia({
        page: pagination.current,
        limit: pagination.pageSize,
        status: 'deleted',
        entityType: filters.entityType !== 'all' ? filters.entityType : undefined,
        mediaType: filters.mediaType !== 'all' ? filters.mediaType : undefined,
        search: filters.search
      }));
    } else if (selectedEntity) {
      dispatch(getEntityMedia({
        entityType: selectedEntity.type,
        entityId: selectedEntity.id
      }));
    }
    dispatch(getMediaStats());
  };

  // Select entity for media management
  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setEntitySelectorVisible(false);
    setActiveTab('entity');
    
    // Load media for this entity
    dispatch(getEntityMedia({
      entityType: entity.type,
      entityId: entity.id
    }));
  };

  // Update media details
  const handleUpdateMedia = async (values) => {
    try {
      await dispatch(updateMedia({ 
        id: editingMedia._id, 
        data: values 
      })).unwrap();
      setEditModalVisible(false);
      setEditingMedia(null);
      handleRefresh();
    } catch (err) {
      // Error handled by useEffect
    }
  };

  // Handle edit media
  const handleEditMedia = (media) => {
    setEditingMedia(media);
    setEditModalVisible(true);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CloudUploadOutlined className="text-2xl text-blue-600" />
            <div>
              <Title level={2} className="m-0">
                Media Manager
              </Title>
              <Text className="text-gray-600">
                Centralized media management for all your projects and blog posts
              </Text>
            </div>
          </div>
          <Space>
            <Button 
              icon={<PlusOutlined />} 
              onClick={() => setEntitySelectorVisible(true)}
              type="primary"
            >
              Select Project/Blog
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          <Overview onEditMedia={handleEditMedia} />
        </TabPane>

        <TabPane 
          tab={selectedEntity ? `${selectedEntity.name} Media` : "Select Entity"} 
          key="entity"
          disabled={!selectedEntity}
        >
          <SelectedEntity 
            selectedEntity={selectedEntity}
            onEditMedia={handleEditMedia}
            onRefresh={handleRefresh}
          />
        </TabPane>

        <TabPane tab="Trash" key="trash">
          <Trash onEditMedia={handleEditMedia} />
        </TabPane>
      </Tabs>

      {/* Entity Selector Modal */}
      <SelectProjOrBlog
        visible={entitySelectorVisible}
        onCancel={() => setEntitySelectorVisible(false)}
        onEntitySelect={handleEntitySelect}
      />

      {/* Edit Media Modal */}
      <Modal
        title="Edit Media"
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingMedia(null);
        }}
        footer={null}
        width={600}
      >
        {editingMedia && (
          <div>
            <div className="mb-4">
              {editingMedia.mediaType === 'image' ? (
                <img
                  src={editingMedia.url}
                  alt={editingMedia.altText}
                  className="w-full h-48 object-cover rounded-lg"
                />
              ) : (
                <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Text className="ml-2">{editingMedia.originalName}</Text>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <Text className="block mb-2 font-medium">Description</Text>
                <TextArea
                  placeholder="Enter media description"
                  defaultValue={editingMedia.description}
                  rows={3}
                  onChange={(e) => setEditingMedia({
                    ...editingMedia,
                    description: e.target.value
                  })}
                />
              </div>
              
              <div>
                <Text className="block mb-2 font-medium">Alt Text (for accessibility)</Text>
                <Input
                  placeholder="Enter alt text for accessibility"
                  defaultValue={editingMedia.altText}
                  onChange={(e) => setEditingMedia({
                    ...editingMedia,
                    altText: e.target.value
                  })}
                />
              </div>
              
              <div>
                <Text className="block mb-2 font-medium">Display Order</Text>
                <Input
                  type="number"
                  placeholder="Display order (lower numbers appear first)"
                  defaultValue={editingMedia.order}
                  onChange={(e) => setEditingMedia({
                    ...editingMedia,
                    order: parseInt(e.target.value) || 0
                  })}
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  type="primary"
                  onClick={() => handleUpdateMedia({
                    description: editingMedia.description,
                    altText: editingMedia.altText,
                    order: editingMedia.order
                  })}
                  loading={processing}
                >
                  Update Media
                </Button>
                <Button
                  onClick={() => {
                    setEditModalVisible(false);
                    setEditingMedia(null);
                  }}
                  disabled={processing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MediaManager;